import React from 'react';
import { specialtyColors, specialtySolidColors } from '../constants/brand';
import { ChevronRightIcon } from './icons';
import { parseLocalDate, formatVisitTime } from '../utils/dateUtils';

const PillIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const ShareCheckIcon = ({ className = "w-3 h-3" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
  </svg>
);

const SummaryCard = ({ appointment, group, onClick, onGroupClick, showDate = true }) => {
  const gradient = specialtyColors[appointment.specialty] || specialtyColors['General'];
  const solidColor = specialtySolidColors[appointment.specialty] || specialtySolidColors['General'];

  const summary = appointment.summary || {};
  const medications = summary.medications || [];
  const headline = summary.headline || null;
  const firstKeyPoint = summary.keyPoints?.[0] || '';
  const displayText = headline || firstKeyPoint;
  const isShared = !!(group && group.members?.length > 1);
  const isPending = appointment.status && appointment.status !== 'complete';

  const formatDateParts = (dateStr) => {
    const date = parseLocalDate(dateStr) || new Date();
    return {
      weekday: date.toLocaleDateString('en-US', { weekday: 'short' }),
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      day: date.getDate(),
    };
  };

  const dateParts = formatDateParts(appointment.date);

  const handleGroupClick = (e) => {
    e.stopPropagation();
    if (onGroupClick && group) {
      onGroupClick(group);
    }
  };

  return (
    <button
      onClick={onClick}
      className="w-full bg-white rounded-2xl shadow-sm border border-slate-100 active:shadow-lg active:border-slate-200 active:-translate-y-0.5 transition-all text-left group overflow-hidden"
    >
      <div className="flex">
        {/* Left accent bar */}
        <div className={`w-1 ${solidColor} flex-shrink-0`}></div>

        <div className="flex-1 p-4 flex items-start gap-3">
          {/* Date column */}
          {showDate && (
            <div className="flex-shrink-0 w-14 text-center">
              <div className="bg-gradient-to-b from-slate-50 to-slate-100/50 rounded-xl py-2 px-1 border border-slate-100">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{dateParts.month}</p>
                <p className="text-2xl font-bold text-slate-800 leading-tight">{dateParts.day}</p>
                <p className="text-[10px] text-slate-400 font-medium">{dateParts.weekday}</p>
              </div>
            </div>
          )}

          {/* Specialty icon - only show if no date */}
          {!showDate && (
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 shadow-sm`}>
              <span className="text-white font-bold text-sm">{appointment.specialty.slice(0, 2).toUpperCase()}</span>
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold text-slate-900 group-active:text-teal-600 transition-colors">{appointment.provider}</h3>
                <p className="text-sm text-slate-500">{[appointment.specialty, formatVisitTime(appointment.time)].filter(Boolean).join(' • ')}</p>
              </div>
              {showDate && (
                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 shadow-sm group-active:scale-110 transition-transform`}>
                  <span className="text-white font-bold text-xs">{appointment.specialty.slice(0, 2).toUpperCase()}</span>
                </div>
              )}
            </div>

            {/* Summary text — headline if available, otherwise first key point */}
            <p className={`text-sm mt-2 line-clamp-2 leading-relaxed ${headline ? 'text-slate-700 font-medium' : 'text-slate-600'}`}>
              {isPending ? (
                <span className="text-slate-400 italic">Summary processing...</span>
              ) : (
                displayText
              )}
            </p>

            {/* Info badges row */}
            <div className="flex items-center gap-2 mt-2.5 flex-wrap">
              {/* Medication count */}
              {medications.length > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-xs font-medium border border-amber-100">
                  <PillIcon className="w-3 h-3" />
                  {medications.length} med{medications.length !== 1 ? 's' : ''}
                </span>
              )}

              {/* Shared status */}
              {isShared ? (
                <span
                  onClick={handleGroupClick}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-50 text-teal-700 rounded-full text-xs font-medium border border-teal-100 active:bg-teal-100 transition-colors cursor-pointer"
                >
                  <ShareCheckIcon className="w-3 h-3" />
                  Shared
                </span>
              ) : (
                !isPending && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-50 text-slate-400 rounded-full text-xs font-medium border border-slate-100">
                    Not shared
                  </span>
                )
              )}

              {/* Processing status */}
              {isPending && (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full text-xs font-medium border border-blue-100">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                  Processing
                </span>
              )}
            </div>

            {/* Care group pill — only when shared */}
            {isShared && (
              <div
                onClick={handleGroupClick}
                className="inline-flex items-center gap-1.5 mt-2.5 px-2.5 py-1.5 bg-slate-50 active:bg-teal-50 border border-slate-200 active:border-teal-200 rounded-full transition-colors cursor-pointer"
              >
                <div className="flex -space-x-1.5">
                  {group.members.slice(0, 3).map((m) => (
                    <div key={m.id} className="w-5 h-5 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-xs">
                      {m.avatar}
                    </div>
                  ))}
                </div>
                <span className="text-xs font-medium text-slate-600 active:text-teal-700">{group.name}</span>
                <ChevronRightIcon className="w-3 h-3 text-slate-400" />
              </div>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

export default SummaryCard;
